// Agent tab content — list of agents for a single type, with available toggle.

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { AddAgentModal } from "./AddAgentModal.js";

interface AgentItem {
  _id: string; name: string; phone: string; available: boolean;
  city?: string; pincode?: string; lab_partner?: string;
  languages?: string[]; specializations?: string[];
  cities_covered?: string[]; services?: string[];
}
interface AgentListResponse { data: AgentItem[]; total: number; page: number; limit: number }

function detail(a: AgentItem): string {
  if (a.city) return a.city;
  if (a.lab_partner) return a.lab_partner;
  if (a.cities_covered?.length) return a.cities_covered.join(", ");
  if (a.languages?.length) return a.languages.join(", ");
  return "";
}

function AgentRow({ agent, onToggle, toggling }: {
  agent: AgentItem; onToggle: (id: string, val: boolean) => void; toggling: boolean;
}): JSX.Element {
  return (
    <tr className="border-b border-slate-50">
      <td className="py-3 pr-4 text-sm text-slate-800 font-medium">{agent.name}</td>
      <td className="py-3 pr-4 text-sm text-slate-500">{agent.phone}</td>
      <td className="py-3 pr-4 text-sm text-slate-500">{detail(agent)}</td>
      <td className="py-3 pr-4">
        <button
          disabled={toggling}
          onClick={() => onToggle(agent._id, !agent.available)}
          className={`text-xs font-medium px-2 py-1 rounded-full transition-colors ${
            agent.available
              ? "bg-navy-50 text-navy-800 hover:bg-navy-100"
              : "bg-slate-100 text-slate-400 hover:bg-slate-200"
          }`}
        >
          {agent.available ? "Available" : "Unavailable"}
        </button>
      </td>
    </tr>
  );
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
export function AgentTab({ agentType }: { agentType: string }): JSX.Element {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [toggling, setToggling] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery<AgentListResponse>({
    queryKey: ["agents", agentType],
    queryFn: () => apiFetch<AgentListResponse>(`/agents?type=${agentType}`),
  });

  const items = data?.data ?? [];

  async function handleToggle(id: string, val: boolean): Promise<void> {
    setToggling(true);
    try {
      await apiFetch(`/agents/${id}`, { method: "PATCH", body: JSON.stringify({ available: val }) });
      void qc.invalidateQueries({ queryKey: ["agents", agentType] });
    } finally { setToggling(false); }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500">{items.length} agent{items.length !== 1 ? "s" : ""}</span>
        <button
          onClick={() => setShowAdd(true)}
          className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors"
        >
          + Add Agent
        </button>
      </div>
      {isLoading && <p className="py-8 text-center text-sm text-slate-400 animate-pulse">Loading…</p>}
      {isError && (
        <div className="py-8 text-center">
          <p className="text-sm text-red-600 mb-2">Failed to load agents.</p>
          <button onClick={() => refetch()} className="text-sm text-navy-700 hover:underline">Retry</button>
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">No agents yet. Add your first agent.</p>
      )}
      {!isLoading && !isError && items.length > 0 && (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium">Phone</th>
              <th className="py-2 pr-4 font-medium">Details</th>
              <th className="py-2 pr-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>{items.map((a) => <AgentRow key={a._id} agent={a} onToggle={(id, v) => void handleToggle(id, v)} toggling={toggling} />)}</tbody>
        </table>
      )}
      {showAdd && (
        <AddAgentModal
          agentType={agentType}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); void qc.invalidateQueries({ queryKey: ["agents", agentType] }); }}
        />
      )}
    </div>
  );
}
